import { useEffect, useRef, useState, type UIEvent } from "react"
import { ChevronRight, Lightbulb } from "lucide-react"
import { cn } from "@/lib/utils"

/**
 * The model's own summary of how it reached an answer, as a collapsible block
 * above that answer.
 *
 * Shown only when reasoning was asked for (any effort but Disabled) and the
 * provider actually returned
 * something. That last part matters: for openai/gpt-5.x OpenRouter returns a
 * *summary*, not the raw chain of thought, and a turn the model barely
 * deliberated on returns nothing at all. So "no reasoning" is the normal case,
 * not an error, and it renders nothing rather than an empty box.
 *
 * Open while it streams, because it is filling the dead air where the answer has
 * not started yet. Collapsed once the answer arrives, because the answer is what
 * the user came for. A manual click wins over both — someone who opened it to
 * read wants it to stay open.
 *
 * The body is capped at about eight lines and scrolls, so a long summary never
 * pushes the chat down. While it is live it tails the newest lines, the way the
 * code snippet beside it does — unless the reader scrolls up, in which case it
 * holds still until they come back to the bottom. A finished block opened by
 * hand starts at the top: once it is done, reading order beats recency.
 */
export interface ReasoningBlockProps {
  /** Accumulated reasoning text. Empty or absent renders nothing. */
  reasoning?: string
  /** The reasoning is still arriving. */
  streaming?: boolean
  /** The answer has started, so the block can fold away. */
  hasContent?: boolean
  className?: string
}

/** Within this many px of the bottom counts as "at the bottom" — about a line and a half. */
const FOLLOW_THRESHOLD_PX = 24

export function ReasoningBlock({
  reasoning,
  streaming = false,
  hasContent = false,
  className,
}: ReasoningBlockProps) {
  // null = nobody has clicked, so the automatic rule applies.
  const [manual, setManual] = useState<boolean | null>(null)

  // How long it thought, measured from the first reasoning we saw to the first
  // content. Held in a ref so a re-render mid-stream cannot restart the clock.
  const startedAt = useRef<number | null>(null)
  const [elapsedMs, setElapsedMs] = useState<number | null>(null)

  const hasReasoning = Boolean(reasoning && reasoning.trim())

  useEffect(() => {
    if (!hasReasoning) return
    if (startedAt.current === null) startedAt.current = Date.now()
    // Freeze the duration the moment the answer starts; it is a fact about the
    // turn, not a live counter.
    if (hasContent && elapsedMs === null) {
      setElapsedMs(Date.now() - startedAt.current)
    }
  }, [hasReasoning, hasContent, elapsedMs])

  // Live: still arriving and nothing to read yet, the phase the block opens
  // itself for. Also the only phase in which the body tails the newest lines.
  const live = streaming && !hasContent
  const expanded = manual ?? live

  // Tail-follow while live, unless the reader scrolled up. The flag is written
  // from the scroll handler and read in the effect only (react-hooks/refs).
  const bodyRef = useRef<HTMLDivElement>(null)
  const followRef = useRef(true)

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const el = event.currentTarget
    followRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < FOLLOW_THRESHOLD_PX
  }

  useEffect(() => {
    if (!live || !expanded) return
    const el = bodyRef.current
    if (el && followRef.current) el.scrollTop = el.scrollHeight
  }, [reasoning, live, expanded])

  if (!hasReasoning) return null

  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <button
        type="button"
        onClick={() => setManual(!expanded)}
        aria-expanded={expanded}
        className={cn(
          "inline-flex items-center gap-1 self-start px-1 -ml-1 rounded-md",
          "text-caption text-muted-foreground hover:text-foreground hover:bg-hover",
          "cursor-pointer transition-colors",
        )}
      >
        <ChevronRight
          size={12}
          strokeWidth={2}
          className={cn("shrink-0 transition-transform", expanded && "rotate-90")}
          aria-hidden="true"
        />
        <Lightbulb size={12} strokeWidth={1.75} className="shrink-0" aria-hidden="true" />
        {live ? "Thinking…" : thoughtLabel(elapsedMs)}
      </button>

      {expanded && (
        <div
          ref={bodyRef}
          onScroll={handleScroll}
          className={cn(
            "text-caption text-muted-foreground whitespace-pre-wrap break-words",
            "border-l-2 border-border-subtle pl-3 ml-1",
            // Capped and scrollable so a long summary never pushes the chat; the
            // live phase tails it like the code snippet next to it.
            "max-h-32 overflow-y-auto overscroll-contain",
          )}
        >
          {reasoning}
        </div>
      )}
    </div>
  )
}

/** "Thought for 4s", or just "Thought" when we never saw the answer start. */
function thoughtLabel(elapsedMs: number | null): string {
  if (elapsedMs === null || elapsedMs < 500) return "Thought"
  const seconds = Math.round(elapsedMs / 1000)
  return seconds < 60 ? `Thought for ${seconds}s` : `Thought for ${Math.round(seconds / 60)}m`
}
